import {  useRef, useState } from "react";
import { BrowserQRCodeReader } from '@zxing/library';
import './App.css';
import logo from './logo.png';

const CARD_MAX_DIMENSION = 1600;
const CARD_JPEG_QUALITY = 0.8;

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("unreadable image")); };
    img.src = url;
  });
}

// Shrinks a camera photo and re-encodes it as JPEG so uploads stay small
// (a few hundred KB) and the server always receives a standard image.
async function compressCardPhoto(file) {
  const img = await loadImage(file);
  const scale = Math.min(1, CARD_MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.naturalWidth * scale);
  canvas.height = Math.round(img.naturalHeight * scale);
  const context = canvas.getContext("2d");
  context.fillStyle = "#fff";
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(img, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("could not encode image")), "image/jpeg", CARD_JPEG_QUALITY);
  });
}

function App() {

    const [company, setCompany] = useState("");
    const [currentCustomer, setCurrentCustomer] = useState(false);
    const [role, setRole] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [telephone, setTelephone] = useState("");
    const [address, setAddress] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [productLine, setProductLine] = useState([]);
    const [department, setDepartment] = useState([]);
    const [reason, setReason] = useState([]);
    const [note, setNote] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [cardPhoto, setCardPhoto] = useState(null);
    const [cardPreview, setCardPreview] = useState("");
    const [cardProcessing, setCardProcessing] = useState(false);
    const formRef = useRef(null)

    async function handleCardSelected(event) {
        const file = event.target.files?.[0]
        event.target.value = ""
        if (!file) return
        setError("")
        if (!file.type.startsWith("image/")) {
          setError("Business card must be a photo or image file.")
          return
        }
        setCardProcessing(true)
        try {
          const photo = await compressCardPhoto(file)
          if (cardPreview) URL.revokeObjectURL(cardPreview)
          setCardPhoto(photo)
          setCardPreview(URL.createObjectURL(photo))
        } catch {
          setError("That photo couldn't be read. Please try taking it again.")
        } finally {
          setCardProcessing(false)
        }
    }

    function removeCardPhoto() {
        if (cardPreview) URL.revokeObjectURL(cardPreview)
        setCardPhoto(null)
        setCardPreview("")
    }

    function hasAnyInput() {
        const textValues = [company, role, name, email, telephone, address, city, state, zip, note]
        return !!cardPhoto ||
          textValues.some(value => value.trim() !== "") ||
          productLine.length > 0 || department.length > 0 || reason.length > 0
    }

    function handleSubmit(event) {
        event.preventDefault()
        if (!formRef.current || submitting || cardProcessing) return
        if (!hasAnyInput()) {
          setError("Enter some information or add a business card photo before submitting.")
          return
        }
        setSubmitting(true)
        setError("")
        const row = {
          "toTop": true,
          "cells": [
              {
                "columnId": 3013839193853828,
                "value": company
              },
              {
                "columnId": 7517438821224324,
                "value": currentCustomer
              },
              {
                "columnId": 1887939287011204,
                "value": role
              },
              {
                "columnId": 6391538914381700,
                "value": name
              },
              {
                "columnId": 4139739100696452,
                "value": email
              },
              {
                "columnId": 8643338728066948,
                "value": telephone
              },
              {
                "columnId": 6936359273975684,
                "value": address
              },
              {
                "columnId": 1306859739762564,
                "value": city
              },
              {
                "columnId": 5810459367133060,
                "value": state
              },
              {
                "columnId": 3558659553447812,
                "value": zip
              },
              {
                "columnId": 1866862540312452,
                "value": productLine?.join(', ')
              },
              {
                "columnId": 8062259180818308,
                "value": department?.join(', ')
              },
              {
                "columnId": 743909786341252,
                "value": reason?.join(', ')
              },
              {
                "columnId": 5247509413711748,
                "value": note
              }
            ]
        }

        const body = new FormData()
        body.append("row", JSON.stringify(row))
        if (cardPhoto) body.append("card", cardPhoto, "business-card.jpg")

        fetch("https://checkin-web-node.azurewebsites.net/addrow",
        {
          method: "POST",
          body
        })
        .then(async res => {
          const data = await res.json().catch(() => ({}))
          if (!res.ok) throw new Error(data.message || `Server returned ${res.status}`)
          return data
        })
        .then(data => {
          // The check-in row was saved even if the photo wasn't, so warn and still reset the form.
          if (data.attachmentError) {
            window.alert(`Check-in saved, but the business card photo didn't upload: ${data.attachmentError}`)
          }
          formRef?.current?.submit()
        })
        .catch(error => {
          console.log('Error:', error)
          setError(`Submission failed: ${error.message}. Please try again.`)
          setSubmitting(false)
        });
    }

    function setProductLineValue(value){
      if(productLine?.includes(value)){
        const filteredArray = productLine.filter(val => val !== value)
        setProductLine(filteredArray)
      }
      else{
        setProductLine(prevVal => [...prevVal, value])
      }
    }

    function setDeparmentValue(value){
      if(department?.includes(value)){
        const filteredArray = department.filter(val => val !== value)
        setDepartment(filteredArray)
      }
      else{
        setDepartment(prevVal => [...prevVal, value])
      }
    }

    function setReasonValue(value){
      if(reason?.includes(value)){
        const filteredArray = reason.filter(val => val !== value)
        setReason(filteredArray)
      }
      else{
        setReason(prevVal => [...prevVal, value])
      }
    }
   
    const [display, setDisplay] = useState("none");
  
    async function scanCode() {
      try {
        setDisplay("block");
        const codeReader = new BrowserQRCodeReader();
        const previewElem = document.querySelector(
          "#test-area-qr-code-webcam video"
        );

        const result = await codeReader.decodeOnceFromVideoDevice(undefined, previewElem);
        const values = result.getText().split('$')

        const ScanResults = {
          Id: values[0] || "",
          First_name: values[1] || "",
          Last_name: values[2] || "",
          Company: values[3] || "",
          Email: values[4] || ""
        };


        setCompany(ScanResults.Company)
        setName(`${ScanResults.First_name} ${ScanResults.Last_name}`)
        setEmail(ScanResults.Email)

        setDisplay("none");
        codeReader.reset()
      } catch (error) {
        console.log(error);
      }
    }

    function endScan(){
      setDisplay("none");
    }

  return (
    <>
    <img src={logo} alt="famouslogo" className='px-4 py-2 h-14'/>
    <form ref={formRef}  onSubmit={handleSubmit} className='px-4 py-4 flex justify-center bg-gray-100'>
          <div className='w-full lg:w-1/2'>
            {/* QR Code Scanner */}
            <div className='flex justify-center'>
              <div id="test-area-qr-code-webcam" style={{ display }}>
              <video className='h-80'></video>
              </div>
              {display === 'none' && <button type='button' onClick={scanCode} className="inline-block shrink-0 rounded-md border border-gray-600 bg-gray-700 px-8 py-3 text-sm font-medium text-white transition">Scan QR Code</button>}
              {display === 'none' && (
                <label className={`inline-block shrink-0 ml-2 rounded-md border border-gray-600 bg-gray-700 px-8 py-3 text-sm font-medium text-white transition ${cardProcessing ? 'opacity-50 pointer-events-none' : 'cursor-pointer'}`}>
                  {cardProcessing ? "Processing..." : cardPhoto ? "Retake Card" : "Card Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="sr-only"
                    disabled={cardProcessing}
                    onChange={handleCardSelected}
                  />
                </label>
              )}
              <button type='button' style={{ display }} onClick={endScan} className="h-7 w-7 ml-1 rounded-full border border-red-500 bg-red-500 text-sm font-medium text-white">X</button>
            </div>
            {cardPreview && (
              <div className='flex items-center justify-center gap-3 mt-3'>
                <img src={cardPreview} alt="Business card" className='h-24 rounded-md border border-gray-400 shadow-sm' />
                <button type='button' onClick={removeCardPhoto} className="rounded-md border border-red-500 px-3 py-1 text-sm font-medium text-red-600">
                  Remove
                </button>
              </div>
            )}

            <div className='sm:flex sm:items-end pb-2'>
              <div className='gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="company" className="block text-md font-medium text-gray-700 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setCompany(event.target.value)}
                />
              </div>
              <div className='flex items-center my-2 mx-2 pt-1'>
                <label htmlFor="currentCustomer" className="text-md font-medium text-gray-700 pr-2">
                  Current Customer
                </label>
                <input
                  type="checkbox"
                  id="currentCustomer"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setCurrentCustomer(!currentCustomer)}
                />
              </div>
            </div>
            <div className=' gap-x-2 my-2 mx-2'>
              <label htmlFor="role" className="block text-md font-medium text-gray-700 mb-1">
                Role
              </label>
              <input
                type="text"
                id="role"
                value={role}
                autoComplete="off"
                className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                onChange={(event) => setRole(event.target.value)}
              />
            </div>
            <div className=' gap-x-2 my-2 mx-2'>
              <label htmlFor="name" className="block text-md font-medium text-gray-700 mb-1">
                Name
              </label>
              <input
                type="text"
                id="name"
                value={name}
                autoComplete="off"
                className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                onChange={(event) => setName(event.target.value)}
              />
            </div>
            <div className='sm:flex sm:items-center'>
              <div className=' gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="email" className="block text-md font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
              <div className=' gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="telephone" className="block text-md font-medium text-gray-700 mb-1">
                  Telephone
                </label>
                <input
                  type="tel"
                  id="telephone"
                  value={telephone}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setTelephone(event.target.value)}
                />
              </div>
            </div>

            <div className='sm:flex sm:items-center'>
              <div className=' gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="address" className="block text-md font-medium text-gray-700 mb-1">
                  Address
                </label>
                <input
                  type="text"
                  id="address"
                  value={address}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setAddress(event.target.value)}
                />
              </div>
              <div className=' gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="city" className="block text-md font-medium text-gray-700 mb-1">
                  City
                </label>
                <input
                  type="text"
                  id="city"
                  value={city}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setCity(event.target.value)}
                />
              </div>
            </div>

            <div className='flex items-center'>
              <div className='gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="state" className="block text-md font-medium text-gray-700 mb-1">
                  State
                </label>
                <input
                  type="text"
                  id="state"
                  value={state}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setState(event.target.value)}
                />
              </div>
              <div className='gap-x-2 my-2 mx-2 grow'>
                <label htmlFor="zip" className="block text-md font-medium text-gray-700 mb-1">
                  Zip Code
                </label>
                <input
                  type="text"
                  id="zip"
                  value={zip}
                  autoComplete="off"
                  className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={(event) => setZip(event.target.value)}
                />
              </div>
            </div>

            {/* Product Line Checkboxes */}
            <div className=' gap-x-2 my-3 mx-2 pt-2'>
              <div className="block text-lg font-medium text-gray-700 mb-1 underline">
                Product Line
              </div>
            </div>
            <div className='flex flex-wrap gap-x-3'>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="erp" className="block text-md font-medium text-gray-700 mb-1">
                  ERP
                </label>
                <input
                  type="checkbox"
                  id="erp"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setProductLineValue("ERP")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="wms" className="block text-md font-medium text-gray-700 mb-1">
                  WMS
                </label>
                <input
                  type="checkbox"
                  id="wms"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setProductLineValue("WMS")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="fis" className="block text-md font-medium text-gray-700 mb-1">
                  FIS
                </label>
                <input
                  type="checkbox"
                  id="fis"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setProductLineValue("FIS")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="bi" className="block text-md font-medium text-gray-700 mb-1">
                  BI
                </label>
                <input
                  type="checkbox"
                  id="bi"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setProductLineValue("BI")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="apps" className="block text-md font-medium text-gray-700 mb-1">
                  Apps
                </label>
                <input
                  type="checkbox"
                  id="apps"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setProductLineValue("Apps")}
                />
              </div>
            </div>

             {/* Department Checkboxes */}
             <div className=' gap-x-2 my-3 mx-2 pt-2'>
              <div className="block text-lg font-medium text-gray-700 mb-1 underline">
                Department
              </div>
            </div>
            <div className='flex flex-wrap gap-x-3'>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="sales" className="block text-md font-medium text-gray-700 mb-1">
                  Sales
                </label>
                <input
                  type="checkbox"
                  id="sales"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setDeparmentValue("Sales")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="support" className="block text-md font-medium text-gray-700 mb-1">
                  Support
                </label>
                <input
                  type="checkbox"
                  id="support"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setDeparmentValue("Support")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="custom" className="block text-md font-medium text-gray-700 mb-1">
                  Custom
                </label>
                <input
                  type="checkbox"
                  id="custom"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setDeparmentValue("Custom")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="ns" className="block text-md font-medium text-gray-700 mb-1">
                  NS
                </label>
                <input
                  type="checkbox"
                  id="ns"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setDeparmentValue("NS")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="billing" className="block text-md font-medium text-gray-700 mb-1">
                  Billing
                </label>
                <input
                  type="checkbox"
                  id="billing"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setDeparmentValue("Billing")}
                />
              </div>
            </div>

            {/* Reason Checkboxes */}
            <div className=' gap-x-2 my-3 mx-2 pt-2'>
              <div className="block text-lg font-medium text-gray-700 mb-1 underline">
                Reason
              </div>
            </div>
            <div className='flex flex-wrap gap-x-3'>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="compliment" className="block text-md font-medium text-gray-700 mb-1">
                  Compliment
                </label>
                <input
                  type="checkbox"
                  id="compliment"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setReasonValue("Compliment")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="issue" className="block text-md font-medium text-gray-700 mb-1">
                  Issue
                </label>
                <input
                  type="checkbox"
                  id="issue"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setReasonValue("Issue")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="request" className="block text-md font-medium text-gray-700 mb-1">
                  Request
                </label>
                <input
                  type="checkbox"
                  id="request"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setReasonValue("Request")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="upgrade" className="block text-md font-medium text-gray-700 mb-1">
                   Upgrade
                </label>
                <input
                  type="checkbox"
                  id="upgrade"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setReasonValue("Upgrade")}
                />
              </div>
              <div className='flex gap-x-1 my-2 mx-2'>
                <label htmlFor="demo" className="block text-md font-medium text-gray-700 mb-1">
                  Demo
                </label>
                <input
                  type="checkbox"
                  id="demo"
                  autoComplete="off"
                  className="w-6 h-6 rounded-md border-gray-500 shadow-sm sm:text-sm"
                  onChange={() => setReasonValue("Demo")}
                />
              </div>
            </div>

            {/* Notes */}
            <div className=' gap-x-2 my-3 mx-2 pt-2'>
              <div className="block text-lg font-medium text-gray-700 mb-1 underline">
                Notes
              </div>
            </div>
            <div className=' gap-x-2 my-2 mx-2'>
              <textarea
                id="note"
                value={note}
                autoComplete="off"
                className="w-full rounded-md border-gray-500 shadow-sm sm:text-sm"
                rows={4}
                placeholder="Enter any additional notes..."
                onChange={(event) => setNote(event.target.value)}
              ></textarea>
            </div>

            {error && (
              <div role="alert" className='my-2 mx-2 rounded-md border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-700'>
                {error}
              </div>
            )}

            <div className='gap-x-2 my-4 mx-2 flex justify-center'>
              <button
                type='submit'
                disabled={submitting || cardProcessing}
                className="inline-block shrink-0 rounded-md border border-[#5eb65a] bg-[#5eb65a] px-12 py-3 text-sm font-medium text-white transition disabled:opacity-50"
              >
                {submitting ? "Submitting..." : "Submit"}
              </button>
            </div>
          </div>
        </form>
        </>
  );
}

export default App;
